import { Button } from '@repo/design-system/components/ds/button';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Checkbox } from '@repo/design-system/components/ui/checkbox';
import { Fieldset } from '@repo/design-system/components/ui/fieldset';
import { Form } from '@repo/design-system/components/ui/form';
import { Modal } from '@repo/design-system/components/ui/modal';
import { Select, SelectItem } from '@repo/design-system/components/ui/select';
import { TextArea } from '@repo/design-system/components/ui/text-area';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useT } from '@repo/i18n/components/i18n.client';
import { useDeleteMapCategoryMutation, useUpdateMapCategoryMutation } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectMapCategories } from '@repo/store/maps/slices/mapData';
import { ColorPicker } from '@repo/ui/components/color-picker/color-picker';
import { useCallback, useMemo, useState } from 'react';
import { CategoryColorPalette } from './category-color-palette';
interface CategoryFormState {
  selectedCategoryId: string;
  categoryName: string;
  displayOrder: string;
  selectedColor: string;
  visibleByDefault: boolean;
  excludeFromChecklists: boolean;
  description: string;
  showDeleteModal: boolean;
}
const initialFormState: CategoryFormState = {
  selectedCategoryId: '',
  categoryName: '',
  displayOrder: '',
  selectedColor: '',
  visibleByDefault: true,
  excludeFromChecklists: false,
  description: '',
  showDeleteModal: false
};
export const EditCategoryForm = () => {
  const {
    t
  } = useT(['game_map']);
  const mapCategories = useAppSelector(selectMapCategories);
  const externalMapId = useAppSelector(selectExternalMapId);
  const [formState, setFormState] = useState<CategoryFormState>(initialFormState);
  const updateFormState = useCallback((updates: Partial<CategoryFormState>) => {
    setFormState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);
  const immediateUpdateFormState = useCallback((updates_0: Partial<CategoryFormState>) => {
    setFormState(prev_0 => ({
      ...prev_0,
      ...updates_0
    }));
  }, []);
  const [updateCategory, {
    isLoading: isUpdating
  }] = useUpdateMapCategoryMutation();
  const [deleteCategory, {
    isLoading: isDeleting
  }] = useDeleteMapCategoryMutation();
  const selectedCategory = useMemo(() => mapCategories?.find(cat => cat.externalId === formState.selectedCategoryId), [mapCategories, formState.selectedCategoryId]);
  const categoryOptions = useMemo(() => mapCategories?.map(category => <SelectItem key={category.externalId} item={{
    label: category.title,
    value: category.externalId
  }}>
                    {category.title}
                </SelectItem>), [mapCategories]);
  const handleSelectCategory = useCallback((categoryId: string) => {
    const category_0 = mapCategories?.find(cat_0 => cat_0.externalId === categoryId);
    if (category_0) {
      immediateUpdateFormState({
        selectedCategoryId: categoryId,
        categoryName: category_0.title,
        displayOrder: category_0.displayOrder == null ? '' : String(category_0.displayOrder),
        selectedColor: category_0.color || '',
        visibleByDefault: category_0.visibleByDefault ?? true,
        excludeFromChecklists: category_0.excludeFromChecklists ?? false,
        description: category_0.description || ''
      });
    }
  }, [mapCategories, immediateUpdateFormState]);
  const handleSave = useCallback(async () => {
    const {
      selectedCategoryId,
      categoryName,
      displayOrder,
      selectedColor,
      visibleByDefault,
      excludeFromChecklists,
      description
    } = formState;
    if (!selectedCategoryId || !categoryName.trim() || !selectedColor || !externalMapId) return;
    try {
      await updateCategory({
        category: {
          externalId: selectedCategoryId,
          title: categoryName,
          displayOrder: displayOrder === '' ? null : Number(displayOrder),
          color: selectedColor,
          visibleByDefault,
          excludeFromChecklists,
          description
        },
        mapExternalId: externalMapId
      }).unwrap();
      toast({
        title: t($_0 => $_0.category_updated),
        description: t($_1 => $_1.category_updated_successfully),
        variant: 'success'
      });
    } catch (error) {
      toast({
        title: t($ => $.failed_to_update_category),
        description: JSON.stringify(error),
        variant: 'warning'
      });
    }
  }, [formState, externalMapId, updateCategory, t]);
  const handleDelete = useCallback(async () => {
    const {
      selectedCategoryId: selectedCategoryId_0
    } = formState;
    if (!selectedCategoryId_0 || !externalMapId) return;
    try {
      await deleteCategory({
        externalCategoryId: selectedCategoryId_0,
        mapExternalId: externalMapId
      }).unwrap();
      immediateUpdateFormState(initialFormState);
      toast({
        title: t($_3 => $_3.category_deleted),
        description: t($_4 => $_4.category_deleted_successfully),
        variant: 'success'
      });
    } catch (error_0) {
      toast({
        title: t($_2 => $_2.failed_to_delete_category),
        description: JSON.stringify(error_0),
        variant: 'warning'
      });
    }
  }, [formState, externalMapId, deleteCategory, immediateUpdateFormState, t]);
  const displayOrderNumber = Number(formState.displayOrder);
  const hasDisplayOrder = formState.displayOrder !== '';
  const hasValidDisplayOrder = !hasDisplayOrder || Number.isInteger(displayOrderNumber) && displayOrderNumber >= 0;
  const hasDuplicateDisplayOrder = hasDisplayOrder && mapCategories?.some(category_1 => category_1.externalId !== formState.selectedCategoryId && category_1.displayOrder === displayOrderNumber);
  const displayOrderError = !hasValidDisplayOrder ? 'Enter a whole number greater than or equal to 0.' : hasDuplicateDisplayOrder ? 'Another category in this map already uses that display order.' : undefined;
  const isFormValid = formState.selectedCategoryId && formState.categoryName.trim() && formState.selectedColor && hasValidDisplayOrder && !hasDuplicateDisplayOrder;
  return <>
            <Form spacing="md" onSubmit={e => {
      e.preventDefault();
      void handleSave();
    }}>
                <Fieldset legend={t($_5 => $_5.edit_category)} legendHidden spacing="md">
                    <Select label={t($_6 => $_6.category)} value={formState.selectedCategoryId} onValueChange={handleSelectCategory} placeholder={t($_7 => $_7.select_category)} size="sm" fullWidth aria-label={t($_8 => $_8.select_category_to_edit)} trigger={<Button color="secondary" size="sm" fullWidth aria-label={t($_9 => $_9.select_category_to_edit)}>
                                {selectedCategory ? selectedCategory.title : t($_10 => $_10.select_category)}
                            </Button>}>
                        {categoryOptions}
                    </Select>

                    {selectedCategory && <>
                            <TextInput fullWidth label={t($_11 => $_11.name)} value={formState.categoryName} onChange={e_0 => updateFormState({
            categoryName: e_0.target.value
          })} placeholder={t($_12 => $_12.category_name)} size="sm" required />
                            <TextInput fullWidth label="Display order" value={formState.displayOrder} onChange={e_1 => updateFormState({
            displayOrder: e_1.target.value
          })} placeholder="Optional" type="number" min={0} step={1} size="sm" errorText={displayOrderError} helperText="Lower numbers appear first. Blank categories sort alphabetically after ordered categories." />
                            <Checkbox id="visible-by-default" label={t($_13 => $_13.visible_by_default)} checked={formState.visibleByDefault} onCheckedChange={details => updateFormState({
            visibleByDefault: !!details.checked
          })} size="sm" />

                            <Checkbox id="exclude-category-from-checklists" label="Exclude category from checklists" checked={formState.excludeFromChecklists} onCheckedChange={details_0 => updateFormState({
            excludeFromChecklists: !!details_0.checked
          })} size="sm" />
                            <FlexColumn gap="8">
                                <ColorPicker label={t($_14 => $_14.color)} value={formState.selectedColor} onChange={color => updateFormState({
              selectedColor: color
            })} size="sm" />
                                <CategoryColorPalette selectedColor={formState.selectedColor} onColorSelect={color_0 => updateFormState({
              selectedColor: color_0
            })} label="Choose custom color, or one from palette" />
                            </FlexColumn>

                            <TextArea fullWidth size="sm" label={t($_15 => $_15.description)} value={formState.description} onChange={e_2 => updateFormState({
            description: e_2.target.value
          })} placeholder="Max 500 characters" maxLength={500} helperText={`${formState.description.length}/500`} />
                        </>}
                </Fieldset>

                <FlexRow gap="4" justify="end">
                    <Button type="button" variant="ghost" onClick={() => updateFormState({
          showDeleteModal: true
        })} color="error" size="sm" disabled={!formState.selectedCategoryId || isDeleting}>
                        {isDeleting ? t($_16 => $_16.deleting) : t($_17 => $_17.delete)}
                    </Button>
                    <Button type="submit" color="success" size="sm" disabled={!isFormValid || isUpdating}>
                        {isUpdating ? t($_18 => $_18.saving) : t($_19 => $_19.save_changes)}
                    </Button>
                </FlexRow>
            </Form>

            <Modal isOpen={formState.showDeleteModal} onOpenChange={() => updateFormState({
      showDeleteModal: false
    })} title={t($_20 => $_20.delete_category)} description={t($_21 => $_21.delete_category_confirmation, {
      title: selectedCategory?.title
    })} size="sm">
                <FlexRow gap="4" justify="end">
                    <Button variant="ghost" onClick={() => updateFormState({
          showDeleteModal: false
        })} color="secondary" size="sm">
                        {t($_22 => $_22.cancel)}
                    </Button>
                    <Button onClick={handleDelete} color="error" size="sm" disabled={isDeleting}>
                        {isDeleting ? t($_23 => $_23.deleting) : t($_24 => $_24.delete)}
                    </Button>
                </FlexRow>
            </Modal>
        </>;
};